/**
 * @GestionalePD — Consensi newsletter lato USER (Sprint 18, RF-05 / §8.9).
 *
 * Mostra le user_entities per cui l'utente ha dato il consenso a ricevere le newsletter,
 * con possibilità di BLOCCARE / SBLOCCARE la ricezione per singola entità.
 *
 * Modello (§11.9):
 *  - consenso = relazione user_entities (entità seguite)
 *  - blocco   = riga in user_blocked_newsletter_entities (esclude anche se seguita)
 * Dati letti da GET /me/newsletter-preferences (DIP-3: la lista entità è veicolata qui).
 *
 * L'email di recapito è SEMPRE users.email, in sola lettura (RB-5).
 */
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Container,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TablePagination,
  TableCell,
  TableRow,
  Typography,
  Box,
  Switch,
  Chip,
  useMediaQuery,
} from '@mui/material';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
//--------------------------------------------------------------------------
import { TablePaginationActions, theme } from '../../../utils/TableStyles';
import { ErrorUI, HttpErrorHandler } from '../../../utils/ErrorHandler';
import { LoadingUI, EmptyPageUI } from '../../../utils/LayoutStyles';
import { entityTypeLabel } from '../../../utils/EntityTypeLabel';
import { usePopup } from '../../../context/PopupContext';
import {
  preferencesApi,
  extractConsentedEntities,
  extractBlockedEntityIds,
} from '../../../services/newsletter/preferencesApi';

export default function NewsletterPage() {
  const { showPopup, setPopUpText } = usePopup();
  const is600Screen = useMediaQuery('(min-width: 600px)');

  const userData = JSON.parse(localStorage.getItem('userData'));

  const [entities, setEntities] = useState([]);
  const [blockedIds, setBlockedIds] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [apiCalled, setApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentProfile, setCurrentProfile] = useState('');

  // Caricamento preferenze + entità consentite
  const getPreferences = () => {
    setErrorMessage('');
    preferencesApi
      .getMine()
      .then((response) => {
        setEntities(extractConsentedEntities(response.data));
        setBlockedIds(extractBlockedEntityIds(response.data));
        setApiCalled(true);
      })
      .catch((error) => {
        setApiCalled(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  useEffect(() => {
    getPreferences();
  }, []);

  // Profilo corrente (guard USER)
  useEffect(() => {
    let data;
    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));
    if (data) setCurrentProfile(data.roles.filter((role) => role.last_used));
  }, []);

  /* ------------------------- Blocco / sblocco entità ------------------------ */
  const isBlocked = (entityId) => blockedIds.includes(entityId);

  const handleToggleEntity = (entityId) => {
    if (!entityId || togglingId) return;
    const currentlyBlocked = isBlocked(entityId);
    setTogglingId(entityId);

    const request = currentlyBlocked
      ? preferencesApi.unblockEntity(entityId)
      : preferencesApi.blockEntity(entityId);

    request
      .then(() => {
        setBlockedIds((prev) =>
          currentlyBlocked ? prev.filter((id) => id !== entityId) : [...prev, entityId]
        );
        showPopup(true, currentlyBlocked ? 'Comunicazioni riattivate' : 'Comunicazioni bloccate');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      })
      .finally(() => setTogglingId(null));
  };

  /* ------------------------------- Pagination ------------------------------- */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - entities.length) : 0;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (currentProfile && currentProfile[0].prof_type_name !== 'USER') return <Navigate to={{ pathname: '/' }} />;

  return (
    <div>
      <Helmet>
        <title>Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        <Card sx={{ padding: '24px', marginBottom: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant="h4">Preferenze comunicazioni</Typography>
            <Typography sx={{ lineHeight: '1.4' }}>
              Qui trovi le entità da cui hai dato il consenso a ricevere le newsletter. Puoi bloccare o riattivare le
              comunicazioni per ciascuna entità. Se vuoi seguire altri Circoli/Federazioni/Forum puoi selezionarli
              tramite la funzione <ControlPointOutlinedIcon sx={{ transform: 'translateY(25%)' }} /> dalla{' '}
              <Link to={'/user/organization'}>struttura organizzativa</Link>.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ lineHeight: '1' }}>La mail utilizzata per le comunicazioni è:</Typography>
              <Typography sx={{ fontWeight: 'bold' }}>{userData?.email}</Typography>
            </Box>
          </Box>
        </Card>

        {apiCalled ? (
          errorMessage.length === 0 ? (
            <Card sx={{ padding: '24px' }}>
              <TableContainer>
                <Table size="small">
                  {entities.length > 0 ? (
                    <TableBody>
                      {(rowsPerPage > 0
                        ? entities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        : entities
                      ).map((entity) => {
                        const blocked = isBlocked(entity.id);
                        return (
                          <TableRow hover key={entity.id}>
                            <TableCell sx={{ width: '120px' }}>
                              {entity.entity_type ? entityTypeLabel(entity, true) : null}
                            </TableCell>
                            <TableCell>{entity.name}</TableCell>
                            <TableCell align="right" sx={{ width: '120px' }}>
                              <Chip
                                size="small"
                                label={blocked ? 'Bloccata' : 'Attiva'}
                                sx={{
                                  fontWeight: 700,
                                  color: blocked ? '#B72136' : '#1B806A',
                                  backgroundColor: blocked ? '#FFE7D9' : '#D8FBDE',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ width: '60px' }} align="right">
                              <Switch
                                checked={!blocked}
                                disabled={togglingId === entity.id}
                                onChange={() => handleToggleEntity(entity.id)}
                                inputProps={{ 'aria-label': `Ricezione comunicazioni da ${entity.name}` }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 55 * emptyRows }}>
                          <TableCell colSpan={4} />
                        </TableRow>
                      )}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={4}>{EmptyPageUI('Nessuna entità seguita')}</TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  {entities.length > rowsPerPage && (
                    <TableFooter>
                      <TableRow>
                        <ThemeProvider theme={theme}>
                          <TablePagination
                            sx={{ borderBottom: 'none' }}
                            rowsPerPageOptions={[]}
                            colSpan={4}
                            count={entities.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Righe per Pagina"
                            ActionsComponent={TablePaginationActions}
                          />
                        </ThemeProvider>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </Card>
          ) : (
            ErrorUI(errorMessage)
          )
        ) : (
          LoadingUI()
        )}
      </Container>
    </div>
  );
}
